import { z } from "zod/v4";

export const DefaultPriceQuerySelectors = {
  PRICE: ".igPrice",
  COMPARE_PRICE: ".igComparePrice",
  INSTALLMENT_PRICE: ".igInstallmentPrice",
  SAVINGS_PRICE: ".igSavingsPrice",
  SAVINGS_PERCENTAGE: ".igSavingsPercentage",
  CART_DISCOUNT_MESSAGE: ".igCartDiscountMessage",
  CART_SAVINGS: ".igCartSavings",
  CART_ORIGINAL_TOTAL: ".igCartOriginalTotal",
} as const;

export const ConfigTypeList = [
  "String",
  "Integer",
  "Float",
  "Boolean",
  "Json",
  "List",
] as const;

export type ConfigType = (typeof ConfigTypeList)[number];

export const ConfigTopicList = [
  "GENERAL",
  "PLUGIN_CONFIG",
  "WEB_UI_SCOPE",
  "UI_CONFIG",
  "ANALYTICS",
  "FEATURE_TOGGLE",
] as const;

export type ConfigTopic = (typeof ConfigTopicList)[number];

export type ConfigurationType<T = string> = {
  topic: ConfigTopic;
  name: T;
  defaultValue: unknown;
  dataType: ConfigType;
};

export const WebUiScopesConfigurationKeyList = [
  "AUDIENCE_TARGETING_ADVANCED",
  "AUDIENCE_TARGETING_BASIC",
  "CHECKOUT_BLOCKS",
  "CONCURRENT_TESTS",
  "CONNECT_SNOWFLAKE",
  "MULTI_CURRENCY_ENABLED",
  "CREATE_CHECKOUT_BLOCK_EXPERIENCE",
  "CREATE_CHECKOUT_BLOCK_TEST",
  "CREATE_COMBO_PRICE_SHIPPING_TEST",
  "CREATE_CONTENT_EXPERIMENT",
  "CREATE_CUSTOM_AUDIENCE",
  "CREATE_CUSTOM_EVENT",
  "CREATE_DISCOUNT_EXPERIMENT",
  "CREATE_MUTUALLY_EXCLUSIVE_EXPERIMENTS",
  "CREATE_OFFER",
  "CREATE_ONSITE_EDITS_EXPERIMENT",
  "CREATE_ONSITE_UPSELL",
  "CREATE_PERSONALIZATION",
  "CREATE_PRICE_EXPERIMENT",
  "CREATE_REDIRECT_EXPERIMENT",
  "CREATE_SHIPPING_EXPERIMENT",
  "CREATE_TEMPLATE_EXPERIMENT",
  "CREATE_THEME_EXPERIMENT",
  "EDIT_ADVANCED_SETTINGS",
  "EDIT_CONTENT_EXPERIMENT",
  "EDIT_DISCOUNT_EXPERIMENT",
  "EDIT_PRICE_EXPERIMENT",
  "EDIT_SHIPPING_EXPERIMENT",
  "PERSONALIZATIONS",
  "POPUP_SLIDEOUT",
  "PRICE_TEST_MANUAL_CSV_UPLOAD",
  "PRICE_TEST_PRODUCT_LIMIT",
  "PRICE_TEST_SUBSCRIPTION_PRODUCTS",
  "PROGRESS_BARS",
  "QUANTITY_BUTTONS",
  "SHIPPING_TEST_CUSTOM_RATE",
  "SHIPPING_TEST_FLAT_RATE",
  "SHIPPING_TEST_FLAT_RATE_WITH_THRESHOLD",
  "SHIPPING_TEST_RATE_LIMIT",
  "SHIPPING_TEST_THRESHOLD_ONLY",
  "SHIPPING_TEST_TIERED_RATE",
  "START_CHECKOUT_BLOCK_TEST",
  "START_CHECKOUT_BLOCK_EXPERIENCE",
  "START_CONTENT_EXPERIMENT",
  "START_DISCOUNT_EXPERIMENT",
  "START_MUTUALLY_EXCLUSIVE_EXPERIMENTS",
  "START_OFFER",
  "START_ONSITE_EDITS_EXPERIMENT",
  "START_ONSITE_UPSELL",
  "START_PERSONALIZATION",
  "START_PRICE_EXPERIMENT",
  "START_REDIRECT_EXPERIMENT",
  "START_SHIPPING_EXPERIMENT",
  "START_TEMPLATE_EXPERIMENT",
  "START_THEME_EXPERIMENT",
  "TEST_GROUP_LIMIT",
  "VIEW_ANALYTICS",
  "VIEW_CONTENT_EXPERIMENT",
  "VIEW_DISCOUNT_EXPERIMENT",
  "VIEW_NEW_DASHBOARD",
  "VIEW_PRICE_EXPERIMENT",
  "VIEW_SHIPPING_EXPERIMENT",
  "VIEW_SITEWIDE_ANALYTICS",
  "VIEW_VOLUME_DISCOUNT",
  "ADD_CUSTOM_METRIC_POST_LAUNCH",
  "EXCLUSION_GROUPS",
  "VIEW_DATA_RECONCILIATION_ANALYTICS",
  "IS_HIGH_VOLUME_ANALYTICS",
  "CREATE_ADVANCED_CONTENT_EXPERIMENT",
  "MULTI_CURRENCY_PRICE_EXPERIMENT",
] as const;

export type WebUiScopesConfigurationKeyType =
  (typeof WebUiScopesConfigurationKeyList)[number];

export const FeatureToggleConfigurationKeyList = [
  "AGENTIC_LOGIC_ENABLED",
  "ADVANCED_AGENTIC_LOGIC_ENABLED",
  "AGENTIC_LOGIC_LEGACY_MIGRATION_COMPLETE",
  "EDIT_CURRENCY_FILTER_FOR_PRICE_TESTS",
  "ENABLE_BUNDLE_PRODUCTS",
  "ENABLE_SCHEDULE_TESTS",
  "FUNCTIONS_INCLUDE_SUBSCRIPTION_PRODUCTS",
  "LIVE_CHAT_ENABLED",
  "MAX_ENABLED_OFFERS",
  "PRICE_SPLITTING_ENABLED",
  "SHOPIFY_FUNCTIONS_ENABLED",
  "SHOPIFY_FUNCTIONS_FOR_PRICING_ENABLED",
  "ENABLE_NOT_UPDATING_PRICES",
  "VIEW_SITEWIDE_ANALYTICS_BETA_PAGE",
  "ENABLE_EXPERIENCE_BUILDER_BETA",
  "ENABLE_EXPERIENCE_BUILDER_AI_MODIFICATIONS",
  "ENABLE_POST_PURCHASE_CHECKOUT",
  "ENABLE_EXPERIENCE_CREATION_AGENT",
  "CUSTOM_SHIPPING_PROVIDERS",
  "ENABLE_AI_QA",
  "ENABLE_SLACK_MEMBER_CONNECT",
  "ENABLE_LEGACY_ONSITE_EDITOR",
  "ENABLE_PURCHASE_LIKELIHOOD_TARGETING",
  "ENABLE_META_ADS_INTEGRATION",
] as const;

export type FeatureToggleConfigurationType =
  (typeof FeatureToggleConfigurationKeyList)[number];

export const AnalyticsConfigurationKeyList = [
  "EXCLUDE_IP_ADDRESSES",
  "EXCLUDE_ORDER_TAGS",
  "MAX_Z_SCORE",
  "USE_ORIGINAL_ORDER_REVENUE",
] as const;

export type AnalyticsConfigurationKeyType =
  (typeof AnalyticsConfigurationKeyList)[number];

export const GeneralConfigurationKeyList = [
  "AUTO_SYNC_COGS",
  "SHIPPING_COST_PER_ORDER",
  "SHOPIFY_PLAN",
  "TRANSACTION_FEE_PCT",
] as const;

export type GeneralConfigurationType =
  (typeof GeneralConfigurationKeyList)[number];

export const PluginConfigConfigurationKeyList = [
  "ADD_IG_DISCOUNT_TO_HREF_QUERY_SELECTORS",
  "ADD_IG_CAMPAIGNS_ATTRS",
  "ADD_IG_ID_TO_CART_ATTRS",
  "ADD_IG_TEST_GROUPS_TO_CART_ATTRS",
  "ADD_TEST_GROUP_ID",
  "ADDITIONAL_CART_DISCOUNT_MESSAGE_SELECTORS",
  "ADDITIONAL_CART_ORIGINAL_TOTAL_SELECTORS",
  "ADDITIONAL_CART_SAVINGS_SELECTORS",
  "ADDITIONAL_COMPARE_PRICE_QUERY_SELECTORS",
  "ADDITIONAL_INSTALLMENT_PRICE_QUERY_SELECTORS",
  "ADDITIONAL_PRICE_QUERY_SELECTORS",
  "ADDITIONAL_SAVINGS_PERCENTAGE_QUERY_SELECTORS",
  "ADDITIONAL_SAVINGS_PRICE_QUERY_SELECTORS",
  "ADDITIONAL_VOLUME_DISCOUNT_QUERY_SELECTORS",
  "CART_TRANSFORM_MODE",
  "COUSINS_MAX_LEVELS_UP",
  "CSS",
  "CURRENCY_FORMAT",
  "CURRENCY_FUNCTION",
  "DISCOUNT_TITLE",
  "DOMAIN",
  "DOWN_SAMPLING",
  "EAGER_UPDATE_DOM",
  "ENABLE_ANTI_FLICKER_SNIPPET",
  "ENABLE_CART_METAFIELDS",
  "ENABLE_RENDER_BLOCKING",
  "GA_CD_INDEX",
  "INTERCEPT_ATC_XHR",
  "IS_HEADLESS_STOREFRONT",
  "JAVASCRIPT",
  "LAZY_CART_UPDATE",
  "LOCALE",
  "MAX_CART_ITEMS",
  "MAX_COOKIE_SIZE",
  "METRICS_SAMPLE_RATE",
  "ONLY_TRACK_WITH_CONSENT",
  "PLUGIN_BRANCH",
  "PLUGIN_FULL_MODE",
  "SHADOW_ELEMENT_SELECTORS",
  "SHIPPING_PROGRESS_BAR_CONFIG",
  "SHOULD_DUPLICATE_PRODUCTS",
  "SHOULD_HIDE_SHIPPING_SUBTEXT",
  "SHOULD_MODIFY_VD_REQUESTS",
  "SHOULD_PULL_COST_FROM_SHOPIFY",
  "SHOULD_REDIRECT",
  "SHOULD_USE_COOKIES",
  "SHOULD_USE_PRODUCT_META",
  "UPDATE_VARIANTS_IN_ATC_FORM",
  "USE_BEACON",
  "USE_BROWSER_LOCALE",
  "USE_PRICE_PROPERTY",
] as const;

export type PluginConfigConfigurationKeyType =
  (typeof PluginConfigConfigurationKeyList)[number];

export const UiConfigurationKeyList = [
  "ENABLE_AI",
  "ENABLE_PRICE_CSV_DOWNLOAD",
  "ONBOARDING_TEST_FLOW",
  "SHOP_CURRENCY",
  "SHOP_WEIGHT_UNIT",
  "SHOULD_SHOW_PROFIT_IN_DASHBOARD",
  "SHOULD_SHOW_SITEWIDE_DASHBOARD",
] as const;

export type UiConfigurationKeyType = (typeof UiConfigurationKeyList)[number];

export const ConfigurationKeyList = [
  ...WebUiScopesConfigurationKeyList,
  ...FeatureToggleConfigurationKeyList,
  ...AnalyticsConfigurationKeyList,
  ...GeneralConfigurationKeyList,
  ...PluginConfigConfigurationKeyList,
  ...UiConfigurationKeyList,
] as const;

export type ConfigurationKey = (typeof ConfigurationKeyList)[number];

export const ConfigurationResponseDtoSchema = z.object({
  // id: z.uuid(),
  organizationId: z.uuid(),
  key: z.string(),
  type: z.preprocess(
    (value: unknown) => (<string>value).replace("\r", ""),
    z.enum(ConfigTypeList)
  ),
  value: z.any(),
  topic: z.preprocess(
    (value: unknown) => (<string>value).replace("\r", ""),
    z.enum(ConfigTopicList)
  ),
  lastUpdateTs: z.number(),
  locked: z.boolean().nullish(),
});

export type PatchConfigurationDto = Omit<ConfigurationDto, "lastUpdateTs">;

export type ConfigurationDto = z.infer<typeof ConfigurationResponseDtoSchema>;

export type ConfigurationOptions = {
  key: Lowercase<ConfigurationKey>;
  value: any;
  locked?: boolean | null;
};
